import { KeysIcon } from "@minket/brand/icons/keys";
import { Button } from "@minket/ui/components/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { useMarkStepDone } from "@/components/onboarding/mark-done";
import { StepPage } from "@/components/onboarding/step-page";
import { FormError } from "@/components/tenancy/field";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/onboarding/keys")({
  component: KeysStep,
});

const loadCreated = () => import("@/components/tenancy/created-keys.ondemand");
const CreatedKeys = lazy(() => loadCreated().then((m) => ({ default: m.CreatedKeys })));

const intro =
  "A live key and a test key for the project's production environment, each shown once, as the line your app's environment needs.";
const asks = [
  {
    title: "Somewhere to keep them",
    text: "Minket keeps only a hash of each key, so it cannot show one again.",
  },
  { title: "A live key", text: "Delivers mail from verified domains." },
  {
    title: "A test key",
    text: "Accepts and renders mail without delivering it, for CI and previews.",
  },
];

function KeysStep() {
  const { project } = useWorkspace();
  const projectId = project?.id ?? "";
  const canCreate = useCan("keys.manage");
  const queryClient = useQueryClient();
  const markDone = useMarkStepDone("keys");
  const environments = useQuery({
    ...apiQuery(endpoints.environments, { projectId }),
    enabled: project !== null,
  });
  const production = environments.data?.data.find((env) => env.slug === "production");
  const create = useMutation({
    mutationFn: async (environmentId: string) => {
      const key = (mode: "live" | "test") =>
        apiRequest(
          endpoints.createKey,
          { projectId },
          {
            body: {
              name: mode === "live" ? "Production" : "Production test",
              environment_id: environmentId,
              mode,
            },
          },
        );
      const live = await key("live");
      const made = { live: { token: live.token ?? "", display: live.display } };
      // The live key exists once its answer arrives; a failed test key must not hide it.
      try {
        const test = await key("test");
        return {
          ...made,
          test: test.token ? { token: test.token, display: test.display } : null,
          testError: null,
        };
      } catch (testError) {
        return { ...made, test: null, testError };
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.keys, { projectId }) });
      markDone();
    },
  });
  const reason = !project
    ? "Create the project first"
    : !canCreate
      ? "Viewers cannot create keys"
      : !production
        ? environments.isPending
          ? "Loading the project's environments"
          : "The project's environments could not be loaded"
        : null;
  const keys = create.data;
  return (
    <StepPage
      path="/onboarding/keys"
      intro={intro}
      asks={asks}
      action={
        reason || keys || !production ? (
          {
            label: "Create keys",
            icon: KeysIcon,
            reason: keys ? "Created; copy them below" : (reason ?? undefined),
          }
        ) : (
          <Button
            loading={create.isPending}
            onPointerEnter={() => void loadCreated()}
            onFocus={() => void loadCreated()}
            onClick={() => create.mutate(production.id)}
          >
            <KeysIcon size={20} aria-hidden />
            Create keys
          </Button>
        )
      }
    >
      <FormError error={create.error ?? keys?.testError} />
      {keys ? (
        <Suspense fallback={null}>
          <CreatedKeys live={keys.live} test={keys.test} />
        </Suspense>
      ) : null}
    </StepPage>
  );
}
